import type { ScaleMenu } from '@/shared/api/model';

export type DashboardPreset = 'operations_overview' | 'credit_portfolio' | 'executive_summary';

export interface DashboardLink {
    key: string;
    label: string;
    path: string;
    description: string;
    group: string;
}

export interface DashboardDefinition {
    key: DashboardPreset;
    title: string;
    subtitle: string;
    audience: string;
    importantLinkKeywords: string[];
    mostUsedLinkKeywords: string[];
}

const dashboardDefinitions: Record<DashboardPreset, DashboardDefinition> = {
    operations_overview: {
        key: 'operations_overview',
        title: 'Operations overview',
        subtitle: 'Track customer activity, overdue exposure, and quick access to daily operational work.',
        audience: 'Branch operations and service teams',
        importantLinkKeywords: ['customer', 'loan', 'repay', 'disbur', 'report'],
        mostUsedLinkKeywords: ['customer', 'loan', 'repay', 'transaction', 'report', 'approval'],
    },
    credit_portfolio: {
        key: 'credit_portfolio',
        title: 'Credit portfolio dashboard',
        subtitle: 'Focus on loan health, arrears pressure, and actions that improve portfolio quality.',
        audience: 'Credit officers and loan supervisors',
        importantLinkKeywords: ['loan', 'repay', 'disbur', 'approval', 'collateral', 'report'],
        mostUsedLinkKeywords: ['loan', 'repay', 'approval', 'disbur', 'collateral', 'portfolio'],
    },
    executive_summary: {
        key: 'executive_summary',
        title: 'Executive summary dashboard',
        subtitle: 'A concise view of portfolio exposure, customer scale, and links that matter most for leadership.',
        audience: 'Managers and primary decision makers',
        importantLinkKeywords: ['report', 'customer', 'loan', 'notification', 'config'],
        mostUsedLinkKeywords: ['report', 'loan', 'customer', 'approval', 'config', 'day end'],
    },
};

const fallbackDescriptions: Record<string, string> = {
    customer: 'Open customer records and servicing tools.',
    loan: 'Review loan lifecycle work and portfolio items.',
    repay: 'Open repayment queues and due-loan actions.',
    disbur: 'Continue loan disbursement work for approved loans.',
    report: 'Run reports and review operational outputs.',
    approval: 'Check pending approval work and decision queues.',
    collateral: 'Manage collateral records and related details.',
    transaction: 'Access transaction processing and daily operations.',
    config: 'Open system configuration and administrative tools.',
    notification: 'Review outbound communication and follow-up triggers.',
    portfolio: 'Inspect portfolio management and transfer activities.',
    'day end': 'Check operational controls and end-of-day functions.',
};

const normalize = (value?: string) => (value || '').trim().toLowerCase();

const formatLabel = (value?: string) => {
    const trimmed = (value || '').trim();
    if (!trimmed) {
        return 'Open workspace';
    }

    return trimmed
        .replace(/[\-_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const matchKeyword = (menu: DashboardLink, keyword: string) => {
    const haystack = [menu.label, menu.group, menu.description, menu.path].map(normalize).join(' ');
    return haystack.includes(normalize(keyword));
};

export const getDashboardDefinition = (preset?: DashboardPreset, role?: string): DashboardDefinition => {
    if (preset && dashboardDefinitions[preset]) {
        return dashboardDefinitions[preset];
    }

    const normalizedRole = normalize(role);

    if (normalizedRole.includes('manager') || normalizedRole.includes('director') || normalizedRole.includes('admin')) {
        return dashboardDefinitions.executive_summary;
    }

    if (normalizedRole.includes('loan') || normalizedRole.includes('credit') || normalizedRole.includes('recovery')) {
        return dashboardDefinitions.credit_portfolio;
    }

    return dashboardDefinitions.operations_overview;
};

export const getDashboardPresetOptions = () => [
    { label: 'Operations overview', value: 'operations_overview' as DashboardPreset },
    { label: 'Credit portfolio', value: 'credit_portfolio' as DashboardPreset },
    { label: 'Executive summary', value: 'executive_summary' as DashboardPreset },
];

export const flattenMenuLinks = (menus: Array<(ScaleMenu & { items?: any[] }) | any>, parentLabel?: string): DashboardLink[] => {
    return (menus || []).flatMap((menu, index) => {
        const label = formatLabel(menu?.label || menu?.code || menu?.id || menu?.page);
        const group = parentLabel || formatLabel(menu?.category || 'Workspace');
        const children = Array.isArray(menu?.items) ? flattenMenuLinks(menu.items, label) : [];
        const key = String(menu?.page || menu?.code || menu?.id || `${label}-${index}`);
        const page = menu?.page;
        const path = typeof page === 'string' && page.trim().length > 0 ? page.trim() : '';
        const descriptionKeyword = Object.keys(fallbackDescriptions).find((keyword) => matchKeyword({ key, label, path, description: '', group }, keyword));
        const description = descriptionKeyword ? fallbackDescriptions[descriptionKeyword] : `Open ${label.toLowerCase()}.`;
        const current = path
            ? [{ key, label, path, description, group } satisfies DashboardLink]
            : [];

        return [...current, ...children];
    });
};

const uniqueByPath = (links: DashboardLink[]) => {
    const seen = new Set<string>();
    return links.filter((link) => {
        if (!link.path || seen.has(link.path)) {
            return false;
        }
        seen.add(link.path);
        return true;
    });
};

export const buildDashboardLinkCollections = (
    definition: DashboardDefinition,
    menuLinks: DashboardLink[],
    favoritePaths: string[]
) => {
    const dedupedLinks = uniqueByPath(menuLinks);
    const favoriteLinks = uniqueByPath([
        ...favoritePaths.map((path) => dedupedLinks.find((link) => link.path === path)).filter(Boolean) as DashboardLink[],
        ...dedupedLinks.filter((link) => definition.importantLinkKeywords.some((keyword) => matchKeyword(link, keyword))).slice(0, 3),
    ]).slice(0, 6);

    const importantLinks = uniqueByPath(
        definition.importantLinkKeywords.flatMap((keyword) => dedupedLinks.filter((link) => matchKeyword(link, keyword)))
    )
        .filter((link) => !favoriteLinks.some((favorite) => favorite.path === link.path))
        .slice(0, 6);

    const mostUsedLinks = uniqueByPath([
        ...definition.mostUsedLinkKeywords.flatMap((keyword) => dedupedLinks.filter((link) => matchKeyword(link, keyword))),
        ...dedupedLinks,
    ])
        .filter((link) => !favoriteLinks.some((favorite) => favorite.path === link.path))
        .filter((link) => !importantLinks.some((important) => important.path === link.path))
        .slice(0, 6);

    return {
        favoriteLinks,
        importantLinks,
        mostUsedLinks,
    };
};
